import { stripCanonicalTrailingSlash } from "../../../../lib/copy/src/canonical-url.mjs";
import { getPlatformConfig } from "@/lib/platform/config";
import { readResolvedPublicMetadata, loadResolvedPublicMetadata, applyResolvedPublicMetadata, projectedHreflang } from "./resolved-public-metadata";
import { seoTitle, seoDescription } from "../../../../lib/copy/src/on-page-seo.mjs";
import { LEGACY_CANONICAL_ORIGIN as legacyCanonicalOrigin } from "../../../../lib/copy/src/public-canonical-client.mjs";
import { isNonProductionSeoHost, noncanonicalSeoCanonical, isAccountSeoPath, NON_INDEXABLE_EDITORIAL_PATHS, REVIEWED_NOINDEX_PATHS, categorySeoTitle } from "./seo-recovery-policy.mjs";
import type { CarCardData } from "@/lib/api";
import { resolveBrandText } from "./runtime-brand-copy.mjs";
import { searchIsNoindex } from "./search-indexation.mjs";
import { buildCarPath, countryCodeToLocale } from "@/lib/api";
import {
  DEFAULT_OG_IMAGE_URL,
  DEFAULT_OG_IMAGE_WIDTH,
  DEFAULT_OG_IMAGE_HEIGHT,
} from "@/lib/site-assets";
import { HIRE_LANDINGS } from "@/content/geo/hireLandings";
import {
  getCanonicalPublicOrigin,
  getRenderedCanonicalPublicOrigin,
  getRenderedCanonicalUrl,
  legacyCanonicalUrl,
} from "@/lib/canonical-origin";

export interface HreflangEntry {
  hreflang: string;
  href: string;
}

export interface PageMeta {
  title?: string;

  description?: string;

  ogImage?: string;

  canonical?: string;

  noIndex?: boolean;
  /**
   * Remove JSON-LD emitted by the static prerender even when this metadata
   * update has no replacement graph. This is reserved for terminal states
   * such as an authoritative car 404, where keeping the live car graph would
   * expose structured data for a page that no longer exists.
   */

  dropPrerenderedJsonLd?: boolean;
  /**
   * Emit `noindex, follow` instead of `noindex, nofollow`. Use this on
   * search-result or filtered pages where the page itself should not be
   * indexed but outbound links to real content pages should still be
   * crawlable (e.g. Help Centre search results linking to articles).
   * Ignored when `noIndex` is also true (noIndex takes precedence with
   * the stricter nofollow directive).
   */

  noIndexFollow?: boolean;

  jsonLd?: Record<string, unknown>;
  /** Car SSR uses the same graph as its browser page; retain the original node. */
  preserveJsonLdIfEqual?: boolean;

  hreflang?: HreflangEntry[];

  ogType?: string;
  /** BCP-47 locale for og:locale, e.g. "en_GB", "de_DE". Optional. */

  ogLocale?: string;
  /** Optional image to <link rel="preload" as="image"> for LCP optimisation. */

  preloadImage?: string;
  /**
   * Responsive-preload pair for the LCP image. When the LCP element is a
   * responsive `<img srcset sizes>` (e.g. the car-detail gallery hero), a
   * fixed-href preload would pick a different candidate than the one the
   * browser actually renders and double-fetch. Supplying `imagesrcset` +
   * `imagesizes` (matching the rendered hero's `srcset`/`sizes`) makes the
   * preload select the exact same candidate, so the byte download is reused.
   * `preloadImage` is still emitted as the `href` fallback for browsers that
   * don't support `imagesrcset`.
   */

  preloadImageSrcset?: string;

  preloadImageSizes?: string;
  /**
   * Per-breakpoint LCP preload split (Task #5918). The car-detail hero paints a
   * DIFFERENT srcset on mobile (capped at 800w, no oversized full variant) vs
   * desktop (full 400/800/2400). A single `imagesrcset` link can only vary its
   * `sizes` per breakpoint, not the candidate set, so on mobile the preload
   * scanner would still pick the oversized 2400w candidate the mobile <img>
   * never paints — a wasted download + double-fetch. When present, each entry is
   * emitted as its own media-scoped `<link rel=preload as=image>` so each
   * breakpoint preloads exactly the candidate its <img> renders. Takes
   * precedence over `preloadImage`/`preloadImageSrcset` when set.
   */

  preloadImageMediaVariants?: Array<{
    href: string;
    imagesrcset?: string;
    imagesizes?: string;
    media?: string;

  }>;

  canonicalPage?: { kind: string; locale?: string; primaryPath?: string };
}

/**
 * Word-boundary meta-description clamp (~155 chars). Google truncates
 * descriptions around 155–160 characters; a hard substring cuts mid-word,
 * which reads as machine-generated in SERPs. Clamps at the last word
 * boundary before the limit and appends an ellipsis only when text was
 * actually removed. Mirrored in scripts/serve-static.mjs
 * (clampDescription) — keep in lockstep.
 */
export function clampDescription(text: string, max = 155): string {
  const t = (text || "").replace(/\s+/g, " ").trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  const clipped = (lastSpace > 40 ? cut.slice(0, lastSpace) : cut).replace(/[\s,;:.\u2013\u2014-]+$/, "");
  return `${clipped}…`;
}

export function normalizeRuntimeBrandText(value: string): string {
  const config = getPlatformConfig();
  return resolveBrandText(value, config.name, getPlatformConfig("bac").name);
}

function metadataPlatform() {
  if (getPlatformConfig().platform === "driveshare") return getPlatformConfig();
  // Host metadata is not the product brand switch: BAC's public identity must
  // not change merely because a component or account selected another brand.
  const host = typeof window === "undefined"
    ? new URL(getCanonicalPublicOrigin()).hostname
    : window.location.hostname || new URL(window.location.origin).hostname;
  return /^(?:www\.|staging\.)?bookaclassic\.com$/i.test(host)
    ? getPlatformConfig("bac") : getPlatformConfig();
}

export function generatePageMeta(meta: PageMeta) {
  const config = metadataPlatform();
  // Defensive: if the caller already branded the title (e.g. landing-page
  // rows seeded from prod include "… | BookAclassic"), don't append the
  // brand a second time. Previously this produced "… | BookAclassic |
  // BookAclassic" titles that were both ugly and over Google's ~60-char
  // display budget. Comparison is case-insensitive on the trailing suffix.
  const normalizedTitle = meta.title
    ? (config.platform === "bac"
      ? meta.title.replace(new RegExp(`\\b${getPlatformConfig("driveshare").name}\\b`, "gi"), config.name)
      : normalizeRuntimeBrandText(meta.title))
    : undefined;
  const brandSuffix = ` | ${config.name}`;
  const alreadyBranded =
    !!normalizedTitle &&
    normalizedTitle.toLowerCase().endsWith(brandSuffix.toLowerCase());
  const title = normalizedTitle
    ? alreadyBranded
      ? normalizedTitle
      : `${normalizedTitle}${brandSuffix}`
    : config.name;
  const description = clampDescription(
    normalizeRuntimeBrandText(meta.description || ""),
  );

  return publicPageHeadModel({
    title: seoTitle(title),
    description: seoDescription(description, normalizedTitle || title),
    ogImage: meta.ogImage,
    canonical: meta.canonical,
    noIndex: meta.noIndex,
    // Structured data can intentionally describe cross-brand relationships
    // (for example CompanyPage provenance). Do not blanket-normalize it;
    // generic callers normalize their own display fields.
    jsonLd: config.platform === "bac" ? bacSiteOrganization(meta.jsonLd) : meta.jsonLd,
  });
}

function bacSiteOrganization(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(bacSiteOrganization);
  if (!value || typeof value !== "object") return value;
  const node = Object.fromEntries(Object.entries(value).map(([key, child]) => [key, bacSiteOrganization(child)]));
  if (node["@type"] === "Organization" && /#organization$/.test(String(node["@id"] || ""))) {
    const origin = legacyCanonicalOrigin;
    node.name = getPlatformConfig("bac").name;
    node["@id"] = `${origin}/#organization`;
    node.url = origin;
    node.logo = `${origin}${getPlatformConfig("bac").logoSrc}`;
  }
  return node;
}

export function applyPageMeta(meta: PageMeta) {
  // An authoritative terminal error must be crawl-safe immediately, even
  // when the successful-page projection is absent or belongs to the previous
  // car. Never fetch/reapply a cached success answer over that error.
  if (meta.noIndex && meta.dropPrerenderedJsonLd) {
    const errorMeta = generatePageMeta(meta);
    document.title = seoTitle(errorMeta.title);
    setMetaTag("description", errorMeta.description);
    setMetaTag("og:title", document.title, "property");
    setMetaTag("og:description", errorMeta.description, "property");
    setMetaTag("robots", "noindex, nofollow");
    removeMetaTag("og:url", "property");
    clearHreflangTags();
    document.querySelectorAll(
      'link[rel="canonical"], link[data-page-preload], script[type="application/ld+json"][data-page], script[type="application/ld+json"][data-prerender]',
    ).forEach(node => node.remove());
    return;
  }
  const publicDecision = typeof window !== "undefined"
    ? readResolvedPublicMetadata(window.location.pathname) : null;
  if (typeof window !== "undefined" && !publicDecision &&
      !isAccountSeoPath(window.location.pathname) &&
      !/^\/(?:[a-z]{2}(?:-[A-Za-z]{2})?\/){0,2}(?:auth|login|sign-?up|verify|checkout|reset-password|forgot-password)(?:\/|$)/.test(window.location.pathname)) {
    const requested = window.location.pathname + window.location.search;
    void loadResolvedPublicMetadata(window.location.pathname).then(answer => {
      if (answer && window.location.pathname + window.location.search === requested)
        applyPageMeta(meta);
    }).catch(error => console.error("Public metadata projection unavailable", error));
    return;
  }
  const resolved = generatePageMeta(publicDecision?{...meta,canonical:publicDecision.canonical}:meta);
  const config = metadataPlatform();
  if (typeof window !== "undefined") {
    resolved.title = categorySeoTitle(resolved.title, window.location.pathname, meta.ogLocale?.split("_")[0]);
    // Category copy can resolve its own runtime brand after generatePageMeta.
    if (config.platform === "bac") resolved.title = resolved.title.replace(
      new RegExp(`\\b${getPlatformConfig("driveshare").name}\\b`, "gi"), config.name);
  }
  resolved.title = seoTitle(resolved.title);
  document.title = resolved.title;

  setMetaTag("description", resolved.description);
  setMetaTag("og:title", resolved.title, "property");
  setMetaTag("og:description", resolved.description, "property");
  const siteUrl = typeof window !== "undefined"
    ? getRenderedCanonicalPublicOrigin(window.location.origin)
    : getCanonicalPublicOrigin();
  const usingDefaultOgImage = !resolved.ogImage;
  // Default branded share card follows the ACTIVE platform (Task #8034):
  // bac keeps the R2-hosted card (the legacy www host doesn't serve this
  // build); the ds brand uses the registry's same-origin 1200×630 card,
  // made absolute so scrapers can fetch it.
  const defaultOgImage =
    config.platform === "driveshare"
      ? new URL(config.ogImageSrc, window.location.origin).toString()
      : DEFAULT_OG_IMAGE_URL;
  const ogImage = resolved.ogImage || defaultOgImage;

  setMetaTag("og:type", meta.ogType || "website", "property");
  setMetaTag("og:site_name", config.name, "property");
  setMetaTag("og:image", ogImage, "property");
  // og:image:width/height are only accurate for the default branded share card
  // (1200×630). Per-page cards (car photos, landing heroes) have varying
  // dimensions, so we drop the hints rather than advertise the wrong size.
  if (usingDefaultOgImage) {
    setMetaTag("og:image:width", String(DEFAULT_OG_IMAGE_WIDTH), "property");
    setMetaTag("og:image:height", String(DEFAULT_OG_IMAGE_HEIGHT), "property");
  } else {
    removeMetaTag("og:image:width", "property");
    removeMetaTag("og:image:height", "property");
  }

  // /us/* soft-handoff SEO guard (task #677): while the cross-brand redirect
  // flag (`feature_us_hide_from_search`) is OFF, /us/* pages render in BAC
  // chrome instead of 404'ing. To avoid indexable duplicate-content variants
  // (e.g. /us self-canonicalising to /us, US car pages canonicalising to
  // /us/cars/...), force `noindex,nofollow` on the entire /us/* tree and
  // strip the /us prefix from the canonical so Google attributes any signal
  // we do leak to the worldwide URL. Once the flag is flipped ON in prod,
  // the server 301 takes over and this guard becomes a no-op (no /us/* page
  // ever renders client-side).
  const onUsPath = typeof window !== "undefined"
    && (window.location.pathname === "/us" || window.location.pathname.startsWith("/us/"))
    && window.location.pathname !== "/us/search";
  const onAccountPath = typeof window !== "undefined" && isAccountSeoPath(window.location.pathname);
  const restrictedHost = typeof window !== "undefined" &&
    isNonProductionSeoHost(window.location.hostname || new URL(window.location.origin).hostname);
  const placeholderPage = typeof window !== "undefined" && NON_INDEXABLE_EDITORIAL_PATHS.has(window.location.pathname.replace(/\/+$/, ""));
  const reviewedNoindex = typeof window !== "undefined" && REVIEWED_NOINDEX_PATHS.has(window.location.pathname.replace(/\/+$/, ""));
  const effectiveCanonical = onAccountPath ? window.location.pathname : onUsPath && resolved.canonical && !resolved.canonical.startsWith("http")
    ? (resolved.canonical === "/us" ? "/" : resolved.canonical.replace(/^\/us(?=\/|$)/, ""))
    : resolved.canonical;

  const fallbackCanonicalUrl = publicDecision?.canonical || (effectiveCanonical
    ? (effectiveCanonical.startsWith("http")
        ? (typeof window !== "undefined"
            ? getRenderedCanonicalUrl(effectiveCanonical, window.location.origin)
            : effectiveCanonical)
        : `${siteUrl}${effectiveCanonical}`)
    : `${siteUrl}${typeof window !== "undefined"
        ? (onUsPath
            ? (window.location.pathname === "/us" ? "/" : window.location.pathname.replace(/^\/us(?=\/|$)/, ""))
            : window.location.pathname)
        : "/"}`);
  const productionCanonical = publicDecision?.canonical || legacyCanonicalUrl(window.location.pathname, fallbackCanonicalUrl);
  const servedHostCanonical = document.querySelector('link[rel="canonical"][data-host-canonical-path]');
  const canonicalUrl = stripCanonicalTrailingSlash(publicDecision?.canonical || (restrictedHost
    ? noncanonicalSeoCanonical(window.location.pathname,
        servedHostCanonical?.getAttribute("data-host-canonical-path") === window.location.pathname
          ? servedHostCanonical.getAttribute("href") : productionCanonical)
    : productionCanonical));
  if (servedHostCanonical?.getAttribute("data-host-canonical-path") !== window.location.pathname) {
    servedHostCanonical?.removeAttribute("data-host-canonical-path");
  }
  const legacyScoped = !publicDecision && (canonicalUrl !== fallbackCanonicalUrl ||
    (canonicalUrl.startsWith(`${legacyCanonicalOrigin}/`) &&
      legacyCanonicalUrl(window.location.pathname, "unscoped") !== "unscoped"));
  setLinkTag("canonical", canonicalUrl);
  setMetaTag("og:url", canonicalUrl, "property");

  setMetaTag("twitter:card", "summary_large_image");
  setMetaTag("twitter:title", resolved.title);
  setMetaTag("twitter:description", resolved.description);
  setMetaTag("twitter:image", ogImage);

  // og:locale (BCP-47 with underscore, e.g. "en_GB"). Optional — pages that
  // know their locale (landing pages, country pages) should pass it; the
  // shell stays silent when omitted rather than guessing the wrong value.
  if (meta.ogLocale) setMetaTag("og:locale", meta.ogLocale, "property");
  else removeMetaTag("og:locale", "property");

  // LCP preload hint for the hero image — emitted as a managed <link rel=
  // "preload" as="image"> in <head>. We tag it with data-page-preload so
  // the next page swap removes the previous hint and we never accumulate
  // stale preloads across client-side navigations.
  document.head
    .querySelectorAll('link[data-page-preload="true"]')
    .forEach((node) => node.remove());
  if (meta.preloadImageMediaVariants && meta.preloadImageMediaVariants.length > 0) {
    // Media-split preload (Task #5918): one media-scoped <link> per breakpoint so
    // each preloads only the candidate its <img> paints (mobile capped at 800w,
    // desktop full 2400w). See PageMeta.preloadImageMediaVariants.
    for (const variant of meta.preloadImageMediaVariants) {
      if (!variant.href) continue;
      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.href = variant.href;
      if (variant.imagesrcset) {
        link.setAttribute("imagesrcset", variant.imagesrcset);
        if (variant.imagesizes) link.setAttribute("imagesizes", variant.imagesizes);
      }
      if (variant.media) link.media = variant.media;
      link.setAttribute("fetchpriority", "high");
      link.setAttribute("data-page-preload", "true");
      document.head.appendChild(link);
    }
  } else if (meta.preloadImage) {
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = meta.preloadImage;
    // When the LCP image is a responsive <img srcset sizes>, attach the same
    // candidate set so the preload picks the exact source the browser will
    // render (no double-fetch). href stays as the non-responsive fallback.
    if (meta.preloadImageSrcset) {
      link.setAttribute("imagesrcset", meta.preloadImageSrcset);
      if (meta.preloadImageSizes) {
        link.setAttribute("imagesizes", meta.preloadImageSizes);
      }
    }
    link.setAttribute("fetchpriority", "high");
    link.setAttribute("data-page-preload", "true");
    document.head.appendChild(link);
  }

  if (!publicDecision) {
  if (restrictedHost) setMetaTag("robots", "noindex, follow");
  else if (!/^(?:www\.)?driveshare\.com$|^(?:events\.driveshare\.com|bookaclassic\.us)$/i.test(
    window.location.hostname || new URL(window.location.origin).hostname) &&
    /^(?:\/[a-z]{2}){0,2}\/search\/?$/.test(window.location.pathname) &&
    searchIsNoindex(window.location.pathname, window.location.search)) setMetaTag("robots", "noindex, follow");
  else if (reviewedNoindex) setMetaTag("robots", "noindex, nofollow");
  else if (resolved.noIndex || onUsPath || onAccountPath || placeholderPage) setMetaTag("robots", "noindex, nofollow");
  else if (meta.noIndexFollow) setMetaTag("robots", "noindex, follow");
  else if (legacyScoped && !onUsPath && !onAccountPath) removeMetaTag("robots");
  else removeMetaTag("robots");

  clearHreflangTags();
  if (!legacyScoped && !resolved.noIndex && !meta.noIndexFollow && !onAccountPath && !onUsPath && !placeholderPage && meta.hreflang && meta.hreflang.length > 0) {
    for (const entry of meta.hreflang) {
      const link = document.createElement("link");
      link.rel = "alternate";
      link.hreflang = entry.hreflang;
      link.href = entry.href;
      link.setAttribute("data-hreflang", "true");
      document.head.appendChild(link);
    }
  }

  } else applyResolvedPublicMetadata(publicDecision);
  const existingLd = document.querySelector('script[type="application/ld+json"][data-page]');
  const nextLd = resolved.jsonLd ? JSON.stringify(publicJsonLdGraph(resolved.jsonLd)) : undefined;
  const servedLd = document.querySelector('script[type="application/ld+json"][data-prerender]');
  let sameLd = false;
  if (meta.preserveJsonLdIfEqual && nextLd && (servedLd || existingLd)) {
    try { sameLd = JSON.stringify(publicJsonLdGraph(JSON.parse((servedLd || existingLd)!.textContent || ""))) === nextLd; } catch {}
  }
  if (existingLd && !sameLd) existingLd.remove();
  if (resolved.jsonLd && !sameLd) {
    // Remove the static-prerendered JSON-LD (`data-prerender`, injected by
    // scripts/serve-static.mjs) only when a real SPA replacement is ready.
    // The prerender ships a valid graph while async car data is loading;
    // removing it earlier creates a temporary JSON-LD-free DOM for crawlers.
    // Once the SPA graph exists, stripping it guarantees exactly one
    // coherent structured-data graph on the rendered page.
    document
      .querySelectorAll('script[type="application/ld+json"][data-prerender]')
      .forEach((node) => node.remove());
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-page", "true");
    script.textContent = nextLd!;
    document.head.appendChild(script);
  } else if (meta.dropPrerenderedJsonLd) {
    // Terminal error states (currently authoritative car 404s) explicitly
    // opt into clearing the prerendered graph because there is no valid page
    // left for it to describe.
    document
      .querySelectorAll('script[type="application/ld+json"][data-prerender]')
      .forEach((node) => node.remove());
  }
}

/**
 * Hreflang map for the driveshare.com/<country>[/<lang>] world.
 *
 * Built from the shared `MARKETS` matrix in `i18n/markets.ts` so we never
 * drift out of sync with the routing/parsing layer. We emit an alternate for
 * EVERY (market × language) combination — not just the per-market default —
 * so /de/en, /fr/en, /ca/fr etc. all advertise themselves correctly to
 * Google. Existing US URLs remain on DRIVESHARE's unprefixed root.
 *
 * Mirror table: `artifacts/api-server/src/routes/seo.ts` (HREFLANG_REGIONS)
 * — they MUST agree.
 */
import { MARKETS, MARKETS_BY_CODE } from "@/i18n/markets";
import {publicPageHeadModel} from "../../../../lib/copy/src/public-page-head.mjs";
import {
  buildCarOfferNode as sourceCarOffer, deriveOfferAvailability as sourceOfferAvailability,
  offerPriceValidUntil as legacyExpiryArithmetic, fuelTypeLabel as sourceFuelLabel,
  normalizeOfferPrice as sourceOfferPrice, normalizeOfferCurrency as sourceOfferCurrency,
  isLandingRichResultEvidence as sourceLandingEvidence, buildLandingRichResultNodes,
  validReviewAggregate as sourceReviewAggregate, publicJsonLdGraph,
} from "../../../../lib/copy/src/public-jsonld.mjs";

const US_MARKET = "us";

interface SupportedRegion {
  hreflang: string;     // "en-GB", "fr-FR", "en-CA", "fr-CA", …
  pathPrefix: string;   // "/uk", "/ca", "/ca/fr", …
  absoluteHost?: string; // set only for cross-brand (US → driveshare.com)
}

export function hreflangFor(market: string, language: string): string {
  // German-market German pages use the language-only tag so the same /de
  // pages target Germany, Austria, and German-speaking Switzerland (no
  // separate de-AT/de-CH alternates exist). The English variant (/de/en)
  // keeps its regional en-DE tag. Mirrors hreflangTag() in
  // api-server/src/lib/locale-url.ts — keep in lock-step.
  if (market === "de" && language === "de") return "de";
  if (market === "no" && language === "no") return "nb-NO";
  if (market === "in" && language.toLowerCase() === "en-in") return "en-IN";
  if (market === "br" && language.toLowerCase() === "pt-br") return "pt-BR";
  // BCP-47 region subtag for the URL market. "uk" → "GB" is the only
  // mismatch we care about; everything else is the ISO-3166 code uppercased.
  const region = market === "uk" ? "GB" : market.toUpperCase();
  return `${language}-${region}`;
}

function pathPrefixFor(market: string, language: string): string {
  const meta = MARKETS_BY_CODE[market];
  if (!meta) return `/${market}`;
  // Default-language combos canonicalise to /<market> with no /<lang>; their
  // alternate href must agree (otherwise we'd advertise a non-canonical URL).
  return language === meta.defaultLanguage ? `/${market}` : `/${market}/${language}`;
}

// Both of these derive from `MARKETS`, which lives in the app-i18n build chunk
// while this file lives in app-lib. Computing them at module-init (a top-level
// IIFE / `const`) is a cross-chunk read that can TDZ-crash the SPA if Rollup's
// chunk init order ever flips (the chunk-cycle white-screen class — see
// scripts/check-import-layering.mjs and i18n/markets is a baselined edge).
// They are instead lazily memoized: computed on first use (inside a function,
// long after every chunk has initialized) and cached. Deterministic, so the
// result is identical to the previous eager value.
let _supportedRegions: SupportedRegion[] | null = null;
function supportedRegions(): SupportedRegion[] {
  if (_supportedRegions) return _supportedRegions;
  const out: SupportedRegion[] = [];
  for (const market of MARKETS) {
    if (market.code === "global") continue;
    if (market.code === US_MARKET) {
      // Existing US routes remain unprefixed on the same DRIVESHARE host.
      out.push({ hreflang: "en-US", pathPrefix: "" });
      continue;
    }
    for (const lang of market.languages) {
      out.push({
        hreflang: hreflangFor(market.code, lang),
        pathPrefix: pathPrefixFor(market.code, lang),
      });
    }
  }
  _supportedRegions = out;
  return out;
}

let _knownMarkets: Set<string> | null = null;
function knownMarkets(): Set<string> {
  return (_knownMarkets ??= new Set(MARKETS.map((m) => m.code)));
}

export function stripLocalePrefix(path: string, locale?: string | null): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  // Prefer an explicit locale hint when provided.
  if (locale) {
    const prefix = `/${locale.toLowerCase()}`;
    if (normalized === prefix) return "/";
    if (normalized.startsWith(`${prefix}/`)) return normalized.slice(prefix.length);
  }
  // Auto-detect /<market>[/<lang>] from the path so callers don't have to
  // thread the locale through every consumer. Reads `languages` from the
  // shared matrix so /ca/fr correctly drops two segments.
  const segs = normalized.replace(/^\/+|\/+$/g, "").split("/").filter(Boolean);
  if (segs.length === 0) return "/";
  const first = segs[0].toLowerCase();
  if (!knownMarkets().has(first)) return normalized;
  const second = segs[1]?.toLowerCase();
  const marketLangs = new Set(MARKETS_BY_CODE[first]?.languages ?? []);
  const isLang = !!(second && marketLangs.has(second));
  const offset = isLang ? 2 : 1;
  const rest = segs.slice(offset).join("/");
  return rest ? `/${rest}` : "/";
}

// Task #7159/#7156 — markets with a curated /{market}/wedding-car-hire
// hire-landing page. Derived from the HIRE_LANDINGS registry (single source
// of truth) so the curated wedding rollout can never drift from the hreflang
// cluster. "uk" is pinned first so x-default points at the original UK page.
export const CURATED_WEDDING_HIRE_MARKETS: readonly string[] = (() => {
  const markets = new Set(
    HIRE_LANDINGS.filter((e) => e.pathType === "wedding").map((e) => e.market),
  );
  return ["uk", ...[...markets].filter((m) => m !== "uk").sort()];
})();
export function buildHreflangEntries(..._input: Parameters<typeof legacyBuildHreflangEntries>): HreflangEntry[] {
  return projectedHreflang();
}
/** Retained historical implementation for C9 removal; no public consumer. */
export function legacyBuildHreflangEntries(path: string, options?: { origin?: string; locale?: string | null }): HreflangEntry[] {
  const base = options?.origin || getCanonicalPublicOrigin();
  const strippedPath = stripLocalePrefix(path, options?.locale);
  // A market homepage strips to "/", but appending that verbatim to a market
  // prefix advertises /uk/, /za/, etc. Those URLs redirect to the canonical
  // non-slash roots. Keep "/" only for the unprefixed US/x-default homepage.
  const alternatePath = strippedPath === "/" ? "" : strippedPath;
  if (strippedPath === "/wedding-car-hire") {
    // Mirror of the special case in scripts/serve-static.mjs
    // buildHreflangEntriesPrerender — keep in lockstep (parity suite).
    const entries: HreflangEntry[] = CURATED_WEDDING_HIRE_MARKETS.map((mkt) => {
      const lang = MARKETS_BY_CODE[mkt]?.defaultLanguage ?? "en";
      return {
        hreflang: hreflangFor(mkt, lang),
        href: `${base}${pathPrefixFor(mkt, lang)}${strippedPath}`,
      };
    });
    entries.push({
      hreflang: "x-default",
      href: entries[0] ? entries[0].href : `${base}${strippedPath}`,
    });
    return entries;
  }
  return [
    ...supportedRegions().map(r => ({
      hreflang: r.hreflang,
      // All global alternates live on the same canonical DRIVESHARE origin.
      href: strippedPath === "/" && r.hreflang === "en-US" &&
        /^(?:www\.)?bookaclassic\.com$/i.test(new URL(base).hostname)
        ? `${base}/us`
        : `${r.absoluteHost || base}${r.pathPrefix}${alternatePath || (r.pathPrefix ? "" : "/")}`,
    })),
    { hreflang: "en", href: `${base}${strippedPath}` },
    { hreflang: "x-default", href: `${base}${strippedPath}` },
  ];
}

/**
 * Task #7947 — restricted hreflang cluster for CAR DETAIL pages.
 *
 * Car pages used to advertise the full ~40-variant all-market cluster on
 * near-identical English content, so Google consolidated the cluster and
 * indexed only one member (sometimes the "wrong" market — see
 * docs/investigations/task7925-duplicate-canonical-findings.md). A car is
 * physically hosted in ONE market; only that market's language variants are
 * meaningfully distinct pages. So we emit alternates ONLY for the hosting
 * market's languages plus the bare x-default, mirroring the sitemap's
 * per-car enumeration (task #865) and the curated hire-landing precedent
 * (task #6522).
 *
 * Mirror: scripts/serve-static.mjs `buildCarHreflangEntriesPrerender` — keep
 * in lockstep (pinned by seo.hreflang.prerender.test.ts).
 *
 * Returns [] (no cluster at all) when the car's country maps to no known
 * market — a single self-canonical URL needs no hreflang.
 */
export function buildCarHreflangEntries(..._input: Parameters<typeof legacyBuildCarHreflangEntries>): HreflangEntry[] {
  return projectedHreflang();
}
export function legacyBuildCarHreflangEntries(
  _path: string,
  _countryCode: string | null | undefined,
  _options?: { origin?: string },
): HreflangEntry[] {
  // All car-language aliases redirect to one country-owned canonical.
  return [];
}

/** A `landing_pages` locale-variant row from `/api/landing-pages/variants`. */
export interface LandingVariantRow {
  locale?: string | null;
  language?: string | null;
  full_path?: string | null;
}

/**
 * Task #7158 (audit D1) — hreflang for DB-driven landing pages built ONLY
 * from the locale variants that actually exist in `landing_pages`, never the
 * all-market fan-out of `buildHreflangEntries` (which advertised ~40 mostly
 * 404 URLs per page). Mirrors the sitemap's per-slug variant grouping in
 * api-server/src/routes/seo.ts (`landingHreflang`) — x-default prefers the
 * English variant, falling back to the first. Returns [] (emit nothing)
 * when the current page itself is not among the variants (reciprocity would
 * break) or when no usable variant exists.
 */
export function buildLandingVariantHreflangEntries(..._input: Parameters<typeof legacyBuildLandingVariantHreflangEntries>): HreflangEntry[] {
  return projectedHreflang();
}
export function legacyBuildLandingVariantHreflangEntries(
  variants: LandingVariantRow[],
  selfPath: string,
  options?: { origin?: string },
): HreflangEntry[] {
  const base = options?.origin || getCanonicalPublicOrigin();
  const normalizedSelf = selfPath.startsWith("/") ? selfPath : `/${selfPath}`;
  const seen = new Set<string>();
  const entries: HreflangEntry[] = [];
  let includesSelf = false;
  let xDefaultHref: string | null = null;
  const hasUsVariant = variants.some(
    (v) => String(v?.locale || "").toLowerCase() === US_MARKET &&
      (`/${String(v.full_path || "").replace(/^\/+|\/+$/g, "")}` || "/") === normalizedSelf,
  );
  if (hasUsVariant) {
    const href = `${base}${normalizedSelf}`;
    return [
      { hreflang: "en-US", href },
      { hreflang: "x-default", href },
    ];
  }
  for (const v of variants) {
    if (!v?.locale || !v.full_path) continue;
    const market = v.locale.toLowerCase();
    // /us/* is force-noindexed sister-brand turf (driveshare.com) — never a
    // valid alternate target on this host.
    if (market === US_MARKET) continue;
    const meta = MARKETS_BY_CODE[market];
    if (!meta) continue;
    const language = (v.language || meta.defaultLanguage || "en").toLowerCase();
    const tag = hreflangFor(market, language);
    if (seen.has(tag)) continue;
    seen.add(tag);
    const path = v.full_path.startsWith("/") ? v.full_path : `/${v.full_path}`;
    const href = `${base}${path}`;
    entries.push({ hreflang: tag, href });
    if (path === normalizedSelf) includesSelf = true;
    if (!xDefaultHref && language === "en") xDefaultHref = href;
  }
  if (entries.length === 0 || !includesSelf) return [];
  entries.push({ hreflang: "x-default", href: xDefaultHref || entries[0].href });
  return entries;
}

function clearHreflangTags() {
  document.querySelectorAll('link[data-hreflang="true"]').forEach(el => el.remove());
}

function setMetaTag(name: string, content: string, attr: "name" | "property" = "name") {
  let tag = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.content = content;
}

function removeMetaTag(name: string, attr: "name" | "property" = "name") {
  const tag = document.querySelector(`meta[${attr}="${name}"]`);
  if (tag) tag.remove();
}

function setLinkTag(rel: string, href: string) {
  document.querySelectorAll(`link[rel="${rel}"]`).forEach((node, index) => {
    if (index > 0) node.remove();
  });
  let tag = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!tag) {
    tag = document.createElement("link");
    tag.rel = rel;
    document.head.appendChild(tag);
  }
  tag.href = href;
}

/**
 * Schema.org `availability` URL for a listing's `Offer`, derived from the
 * listing's offered rental modes. The legacy availability-window date bounds
 * are returned for editing but are not enforced by the quote/booking path.
 * Date-specific blocks are checked when dates are selected, not as a global
 * stock signal for an undated Offer.
 */
export function deriveOfferAvailability(
  hasActiveRental: boolean,
): "https://schema.org/InStock" | "https://schema.org/OutOfStock" {
  return sourceOfferAvailability(hasActiveRental);
}

/**
 * Compatibility arithmetic API only. This is not a source expiry and offer
 * constructors must not use it to manufacture listing freshness.
 */
export function offerPriceValidUntil(from: Date = new Date()): string {
  return legacyExpiryArithmetic(from);
}

/**
 * Human-readable English label for the internal `cars.fuel_type` smallint
 * enum, for structured-data output (JSON-LD `fuelType`). The authoritative
 * enum table is the listing wizard's fuel `<Select>`
 * (SubStepVehicleDetails.tsx / the AI-enrichment writer): 1=Petrol 2=Diesel
 * 3=Electric 4=Hybrid 5=LPG 6=CNG 7=Hydrogen 0=Other. Structured data must
 * be stable English — never the raw enum, never locale-translated. Returns
 * `null` for unknown values AND for 0 ("Other" conveys nothing to a
 * parser), so callers omit `fuelType` entirely. Mirrors `fuelTypeLabel` in
 * `scripts/seo-jsonld.mjs` — keep in lockstep.
 */
export function fuelTypeLabel(fuelType: unknown): string | null {
  return sourceFuelLabel(fuelType);
}

export interface CarOfferInput {
  currencyCode: string | null | undefined;
  rentalDrivingActive: boolean;
  rentalChauffeuredActive: boolean;
  rentalEventActive: boolean;
  priceDriving: number | string | null | undefined;
  chauffeuredHourlyRate: number | string | null | undefined;
  eventHourlyRate: number | string | null | undefined;
  priceEvent: number | string | null | undefined;
  chauffeurMinimum?: number | string | null;
  eventMinimum?: number | string | null;
  minimumPrice?: number | string | null;
  /** Real source expiry only; never synthesize a future freshness date. */
  priceValidUntil?: string;
  publicUnavailable?: boolean;
  /** Stable `@id` for the offer node (e.g. `${carUrl}#offer`). */
  offerId: string;
  /** Canonical listing URL. */
  url: string;
  /** Wizard preview iframe: keep the legacy zero-price single offer. */
  preview?: boolean;
}

/**
 * Google expects Offer prices to be JSON numbers in a plain decimal format.
 * PostgreSQL NUMERIC values can arrive at runtime as strings despite the
 * generated TypeScript types, so coerce only unformatted decimal strings and
 * reject currency symbols, thousands separators, units, infinities and
 * non-positive values.
 *
 * Mirrored in scripts/seo-jsonld.mjs — parity tests keep both paths aligned.
 */
export function normalizeOfferPrice(value: unknown): number | null {
  return sourceOfferPrice(value);
}

/**
 * Listing writes derive currency from country server-side, so structured data
 * must never guess a replacement currency. Normalize a trustworthy ISO-shaped
 * code; otherwise omit the offer until the source listing is corrected.
 *
 * Mirrored in scripts/seo-jsonld.mjs — parity tests keep both paths aligned.
 */
export function normalizeOfferCurrency(value: unknown): string | null {
  return sourceOfferCurrency(value);
}

/**
 * Build the `offers` node for a car listing's Product/Vehicle JSON-LD
 * (task #3733). Derives a valid price/currency/time-unit from the
 * listing's *actual* active rental types — mirroring the BookingWidget's
 * per-tab "from" price (self-drive → per day, chauffeur/event → per hour)
 * — so chauffeured-only and event-only listings publish a real offer
 * instead of the missing/£0 self-drive price the page used to hardcode.
 *
 * - One priced rental type → a single `Offer` with a `UnitPriceSpecification`.
 * - Multiple priced rental types → an `AggregateOffer` carrying the
 *   low/high range plus the individual per-type `Offer`s (each keeping its
 *   own per-day/per-hour unit) so the price range and the units both stay
 *   correct.
 * - No priced rental type → `null` (caller omits the offer rather than
 *   advertising a £0 price).
 *
 * Returns `null` when there is no valid price to publish.
 */
export function buildCarOfferNode(input: CarOfferInput): Record<string, unknown> | null {
  return sourceCarOffer(input);
}

/**
 * Build the inline `offers` node for a list-page (search results / ccTLD
 * landing ItemList) `Product` from a `CarCardData`, reusing
 * `buildCarOfferNode` so every list surface publishes the same real
 * price / availability / `priceValidUntil` as the detail page (task
 * #3741) instead of the old hardcoded `https://schema.org/InStock` +
 * self-drive-day-rate-only offer. Chauffeured-only / event-only cards
 * therefore surface their per-hour (or per-day) "from" price and a
 * correct unit, and a card with no priced active rental mode resolves to
 * `null` so the caller omits the offer rather than advertising £0.
 *
 * Like detail offers, card offers use active rental modes, not the legacy
 * availability-window end. Date-specific bookability is resolved at quote time.
 */
function buildCardOfferNode(car: CarCardData, url: string): Record<string, unknown> | null {
  return buildCarOfferNode({
    currencyCode: car.currencyCode,
    rentalDrivingActive: car.rentalDrivingActive,
    rentalChauffeuredActive: car.rentalChauffeurActive,
    rentalEventActive: car.rentalEventActive,
    priceDriving: car.priceDriving,
    chauffeuredHourlyRate: car.chauffeuredHourlyRate,
    eventHourlyRate: car.eventHourlyRate,
    chauffeurMinimum:car.chauffeurMinPrice,eventMinimum:car.eventMinPrice,minimumPrice:car.minimumPrice,
    priceEvent: car.priceEvent,
    offerId: `${url}#offer`,
    url,
  });
}

function validReviewAggregate(rating: unknown, count: unknown): boolean {
  return sourceReviewAggregate(rating,count);
}

export interface LandingRichResultCar {
  name: string;
  url: string;
  town: string;
  year: number;
  price: number;
  priceUnit?: "day" | "hour" | "minimum";
  /** Authoritative market currency; never infer or coerce it on the client. */
  currency: string;
  image: { url: string; width: number; height: number; alt: string };
  /** Present only when this specific listed car has review evidence. */
  aggregateRating?: { ratingValue: number; reviewCount: number };
}

export interface LandingRichResultEvidence {
  /** Explicit server authority to publish this snapshot as rich-result inventory. */
  eligible: true;
  /** Complete, deterministic inventory selected for this landing page. */
  cars: LandingRichResultCar[];
  /** The exact factual questions rendered beside this inventory. */
  faqs: { q: string; a: string }[];
  /** Factual inventory summary rendered beside the cards and in Service JSON-LD. */
  summary: string;
  /** Canonical factual detail copy; SSR and hydration render this verbatim. */
  details: { heading: string; body: string }[];
  /** Server-resolved target, used to describe the Service without URL guesses. */
  pageScope: string;
  serviceType: string;
  areaServed: string;
  /** Present only when inventory is the explicitly disclosed same-market fallback. */
  fallback?: { sameMarket: true; label: string };
}

/** Reject the complete enrichment rather than publishing a misleading subset. */
export function isLandingRichResultEvidence(value: unknown): value is LandingRichResultEvidence {
  return sourceLandingEvidence(value);
}

/**
 * UK wedding inventory is protected by the server-owned rich-result evidence
 * gate. This includes the explicit English route and any descendants, while
 * preserving legacy ItemLists for every other landing page.
 */
function isProtectedUkWeddingLandingPath(path: string): boolean {
  const normalized = (path.startsWith("/") ? path : `/${path}`)
    .replace(/\/+$/, "")
    .toLowerCase();
  return /^\/uk(?:\/en)?\/wedding-car-hire(?:\/|$)/.test(normalized);
}

export function generateLandingPageJsonLd(page: {
  name: string;
  page_title?: string | null;
  page_description?: string | null;
  seo_meta_description?: string | null;
  full_path: string;
  page_type?: string | null;
}, cars?: CarCardData[], faqs?: { q: string; a: string }[], richEvidence?: unknown, origin = getCanonicalPublicOrigin()): Record<string, unknown> {
  const config = getPlatformConfig();
  const siteUrl = new URL(origin).origin;
  const canonicalPath = page.full_path.startsWith("/") ? page.full_path : `/${page.full_path}`;
  const pageUrl = `${siteUrl}${canonicalPath}`;
  const description = page.seo_meta_description || page.page_description || config.description;

  const graph: Record<string, unknown>[] = [
    {
      "@type": "WebPage",
      "@id": pageUrl,
      name: page.page_title || page.name,
      description,
      url: pageUrl,
      isPartOf: {
        "@type": "WebSite",
        name: config.name,
        url: siteUrl,
      },
    },
    {
      "@type": "Organization",
      "@id": `${siteUrl}/#organization`,
      name: config.name,
      url: siteUrl,
      logo: /^https?:\/\//i.test(config.logoSrc)
        ? config.logoSrc
        : `${siteUrl}${config.logoSrc}`,
    },
  ];

  const evidence = isLandingRichResultEvidence(richEvidence) ? richEvidence : null;
  if (page.page_type === "city" || page.page_type === "brand" || page.page_type === "category" || evidence) {
    const service: Record<string, unknown> = {
      "@type": "Service",
      name: page.page_title || page.name,
      description,
      url: pageUrl,
      provider: {
        "@type": "Organization",
        "@id": `${siteUrl}/#organization`,
        name: config.name,
        url: siteUrl,
      },
    };
    if (page.page_type === "city") service.areaServed = { "@type": "Place", name: page.name };
    if (evidence) {
      service.name = evidence.pageScope;
      service.description = evidence.summary;
      service.serviceType = evidence.serviceType;
      service.areaServed = { "@type": "Place", name: evidence.areaServed };
      service.offers = buildLandingRichResultNodes(evidence,siteUrl,pageUrl)!.aggregateOffer;
    }
    graph.push(service);
  }

  // Rich evidence is also the source for the cards rendered by LandingPage.
  // Once it validates, do not publish the independently fetched search results:
  // doing so would create a second ItemList for cars that are not visible.
  // UK wedding landing paths are stricter: no evidence (including explicitly
  // ineligible or invalid evidence) means no inventory graph at all. Other
  // landing pages retain their legacy search-backed list unchanged.
  const suppressLegacyInventory = isProtectedUkWeddingLandingPath(canonicalPath);
  if (!evidence && !suppressLegacyInventory && cars && cars.length > 0) {
    graph.push({
      "@type": "ItemList",
      numberOfItems: cars.length,
      itemListElement: cars.filter(car => buildCarPath(car)).map((car, index) => {
        const carUrl = `${siteUrl}${buildCarPath(car)}`;
        const offers = buildCardOfferNode(car, carUrl);
        return {
          "@type": "ListItem",
          position: index + 1,
          item: {
            "@type": offers || validReviewAggregate(car.reviewsRating, car.reviewsCount) ? "Product" : "Vehicle",
            name: `${car.year} ${car.make} ${car.model}`,
            url: carUrl,
            ...(car.primaryImageUrl ? { image: car.primaryImageUrl } : {}),
            ...(offers ? { offers } : {}),
            ...(validReviewAggregate(car.reviewsRating, car.reviewsCount)
              ? {
                  aggregateRating: {
                    "@type": "AggregateRating",
                    ratingValue: car.reviewsRating,
                    reviewCount: car.reviewsCount,
                  },
                }
              : {}),
          },
        };
      }),
    });
  }

  graph.push({
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: siteUrl },
      { "@type": "ListItem", position: 2, name: page.page_title || page.name, item: pageUrl },
    ],
  });

  // Programmatic-GEO pages ship an FAQ block; mirror it as
  // FAQPage JSON-LD so AI search engines (and Google rich results) can
  // ingest the Q&A pairs alongside the WebPage / Service / ItemList.
  const visibleFaqs = evidence ? evidence.faqs : faqs;
  if (visibleFaqs && visibleFaqs.length > 0) {
    graph.push({
      "@type": "FAQPage",
      mainEntity: visibleFaqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }

  // These ImageObjects are backed by the image, dimensions and alt text in the
  // visible eligible-car cards. Never infer dimensions from a CDN URL.
  if (evidence) {
    graph.push(buildLandingRichResultNodes(evidence,siteUrl,pageUrl)!.itemList);
  }

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
}

export function generateSearchResultsJsonLd(cars: CarCardData[]): Record<string, unknown> {
  const config = getPlatformConfig();
  const siteUrl = getCanonicalPublicOrigin();

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: cars.filter(car => buildCarPath(car)).map((car, index) => {
      const carUrl = `${siteUrl}${buildCarPath(car)}`;
      const offers = buildCardOfferNode(car, carUrl);
      return {
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": offers || validReviewAggregate(car.reviewsRating, car.reviewsCount) ? "Product" : "Vehicle",
          name: `${car.year} ${car.make} ${car.model}`,
          url: carUrl,
          ...(car.primaryImageUrl ? { image: car.primaryImageUrl } : {}),
          ...(offers ? { offers } : {}),
          ...(validReviewAggregate(car.reviewsRating, car.reviewsCount)
            ? {
                aggregateRating: {
                  "@type": "AggregateRating",
                  ratingValue: car.reviewsRating,
                  reviewCount: car.reviewsCount,
                },
              }
            : {}),
        },
      };
    }),
  };
}
